/**
 * BareMC Internationalization (i18n) System
 * Supports: Danish (da), English (en), German (de)
 * 
 * Translations are stored in separate JSON files under /src/locales/
 */

import da from '../locales/da.json';
import en from '../locales/en.json';
import de from '../locales/de.json';
import es from '../locales/es.json';
import fr from '../locales/fr.json';
import it from '../locales/it.json';
import pl from '../locales/pl.json';
import ar from '../locales/ar.json';

// All translations organized by language code
export const translations = { da, en, de, es, fr, it, pl, ar };

// Supported languages with their display info
export const SUPPORTED_LANGUAGES = [
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
];

// Domain to language mapping
const DOMAIN_LANGUAGE_MAP = {
  'baremc.dk': 'da',
  'baremc.com': 'en',
  'baremc.de': 'de',
  'baremc.es': 'es',
  'baremc.fr': 'fr',
  'baremc.it': 'it',
  'baremc.pl': 'pl',
};

// Detect language from browser/domain
const detectLanguage = () => {
  if (typeof window === 'undefined') return 'da';
  
  // Check domain first
  const hostname = window.location.hostname;
  for (const [domain, lang] of Object.entries(DOMAIN_LANGUAGE_MAP)) {
    if (hostname.includes(domain)) return lang;
  }
  
  // Check browser language
  const browserLang = navigator.language?.substring(0, 2);
  if (translations[browserLang]) return browserLang;
  
  return 'da'; // Default to Danish
};

// Get current language
export const getCurrentLanguage = () => {
  if (typeof window === 'undefined') return 'da';
  const saved = localStorage.getItem('baremc-language');
  return saved || detectLanguage();
};

// Get current search country from localStorage  
export const getCurrentSearchCountry = () => {
  if (typeof window === 'undefined') return 'DK';
  const saved = localStorage.getItem('baremc-search-country');
  return saved || 'DK';
};

// Set language
export const setLanguage = (code) => {
  localStorage.setItem('baremc-language', code);
  window.dispatchEvent(new CustomEvent('languageChange', { detail: code }));
};

// Set search country
export const setSearchCountry = (code) => {
  localStorage.setItem('baremc-search-country', code);
  window.dispatchEvent(new CustomEvent('searchCountryChange', { detail: code }));
};

// Translation function - returns translation for current language
// Fallback chain: current language → English → key
export const t = (key, lang = null) => {
  const currentLang = lang || getCurrentLanguage();
  const keys = key.split('.');

  const lookup = (langCode) => {
    let val = translations[langCode];
    for (const k of keys) {
      if (val && val[k] !== undefined) val = val[k];
      else return undefined;
    }
    return typeof val === 'string' ? val : undefined;
  };

  return lookup(currentLang) ?? lookup('en') ?? key;
};

// Hook for using translations in React components
export const useTranslation = () => {
  const lang = getCurrentLanguage();
  return {
    t: (key) => t(key, lang),
    lang,
    setLanguage,
  };
};

// Language names for Farli AI prompts
export const FARLI_LANGUAGE_INSTRUCTIONS = {
  da: 'Svar på dansk. Du er Farli, en venlig MC-assistent.',
  en: 'Respond in English. You are Farli, a friendly MC assistant.',
  de: 'Antworte auf Deutsch. Du bist Farli, ein freundlicher Motorrad-Assistent.',
  es: 'Responde en español. Eres Farli, un asistente de motos amigable.',
  fr: 'Réponds en français. Tu es Farli, un assistant moto sympathique.',
  it: 'Rispondi in italiano. Sei Farli, un assistente moto amichevole.',
  pl: 'Odpowiadaj po polsku. Jesteś Farli, przyjaznym asystentem motocyklowym.',
  ar: 'Respond in Arabic (العربية). You are Farli, a friendly MC assistant. Write in Arabic with RTL direction.',
};

export default translations;
